import {
  AssignmentMomentDuration,
  AverageAssignmentDurationModel,
} from 'api/model';
import {
  useAverageAssignmentDurationsGetAssignmentMomentDurations,
  useAverageAssignmentDurationsGetAverageAssignmentDurations,
} from 'api/average-assignment-durations/average-assignment-durations';
import Input from 'components/inputs/Input';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import useMe from 'contexts/authentication/useMe';
import useTranslations from 'contexts/basicData/useTranslations';
import Roles, { includesRoles } from 'constants/Roles';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 14px 16px;
  max-width: 1100px;
`;

const Explainer = styled.div`
  padding: 12px 14px;
  border: 1px solid #b5b3b35c;
  border-left: 4px solid rgb(204, 102, 51);
  border-radius: 4px;
  background: #fff;
  line-height: 1.5;

  code {
    background: rgb(235, 235, 235);
    padding: 1px 6px;
    border-radius: 3px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
`;

const SectionHint = styled.p`
  margin: 2px 0 0;
  font-size: 0.85em;
  color: rgb(120, 120, 120);
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;

  th {
    text-align: left;
    font-size: 0.8em;
    color: rgb(120, 120, 120);
    padding: 4px 8px;
    border-bottom: 1px solid rgb(190, 190, 190);
  }

  td {
    padding: 5px 8px;
    border-bottom: 1px solid #b5b3b35c;
  }

  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  tbody tr:hover {
    background: #ecf6ff;
  }
`;

const ScopePill = styled.span<{ tone: 'granular' | 'pair' | 'fallback' }>`
  padding: 1px 8px;
  border-radius: 3px;
  font-size: 0.85em;
  font-weight: 600;
  background: ${({ tone }) =>
    tone === 'granular'
      ? 'rgba(0,187,0,0.15)'
      : tone === 'pair'
        ? 'rgba(0,122,255,0.12)'
        : 'rgb(235,235,235)'};
  color: ${({ tone }) =>
    tone === 'granular'
      ? 'rgb(0,110,0)'
      : tone === 'pair'
        ? 'rgb(0,90,190)'
        : 'rgb(120,120,120)'};
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
`;

const NoAccess = styled.div`
  padding: 40px;
  color: rgb(120, 120, 120);
`;

const currentMonthValue = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}`;
};

const EstimateDurationsPage: React.FC = () => {
  const me = useMe();
  const translations = useTranslations();
  const isSuperAdmin = includesRoles([Roles.SuperAdmin], me?.roles);

  const [month, setMonth] = useState<string>(currentMonthValue());
  const [routeFilter, setRouteFilter] = useState('');

  const { data: momentDurations, isLoading: loadingMoments } =
    useAverageAssignmentDurationsGetAssignmentMomentDurations({
      query: { enabled: isSuperAdmin },
    });
  const { data: averageDurations, isLoading: loadingAverages } =
    useAverageAssignmentDurationsGetAverageAssignmentDurations(
      { yyyyMM: month },
      { query: { enabled: isSuperAdmin } }
    );

  const sortedMoments = useMemo(
    () =>
      [...(momentDurations ?? [])].sort(
        (a, b) =>
          (a.caseTypeID ?? -1) - (b.caseTypeID ?? -1) ||
          (a.assignmentTypeID ?? -1) - (b.assignmentTypeID ?? -1) ||
          Number(a.isLargeCityArea ?? -1) - Number(b.isLargeCityArea ?? -1)
      ),
    [momentDurations]
  );

  const filteredAverages = useMemo(() => {
    const filter = routeFilter.trim().toLowerCase();
    const rows = averageDurations ?? [];
    if (!filter) return rows;
    return rows.filter((r: AverageAssignmentDurationModel) =>
      `${r.fromCounty} ${r.toCounty} ${r.fromZipCode} ${r.toZipCode}`
        .toLowerCase()
        .includes(filter)
    );
  }, [averageDurations, routeFilter]);

  const scopeOf = (row: AssignmentMomentDuration): React.ReactNode => {
    if (row.caseTypeID == null) return <ScopePill tone="fallback">Global</ScopePill>;
    if (row.assignmentTypeID == null)
      return <ScopePill tone="fallback">Hela casetypen</ScopePill>;
    if (row.isLargeCityArea == null)
      return <ScopePill tone="pair">Casetyp + uppdragstyp</ScopePill>;
    return (
      <ScopePill tone="granular">
        {row.isLargeCityArea ? 'Storstadslän' : 'Övriga landet'}
      </ScopePill>
    );
  };

  if (!isSuperAdmin) {
    return <NoAccess>Den här vyn kräver SuperAdmin-behörighet.</NoAccess>;
  }

  return (
    <Page>
      <Explainer>
        <b>Så räknas automatiska tidsestimat fram:</b> för uppdrag som saknar bokad tid
        föreslår systemet <code>Google-körtid + momentschablon</code>. Körtiden hämtas
        från Google Routes en gång per uppdrag och sparas. Momentschablonen är den lärda
        mediantiden för allt <i>utöver</i> körningen — lastning, provkörning, kundtid —
        beräknad varje måndag från de senaste 6 månadernas genomförda, avvikelsefria
        uppdrag. Ju mer specifik rad som har tillräckligt underlag, desto mer träffsäker
        schablon används (storstad → casetyp+uppdragstyp → casetyp → global).
      </Explainer>

      <div>
        <SectionTitle>Momentschabloner (tid utöver körtid)</SectionTitle>
        <SectionHint>
          Median av faktisk arbetstid minus Google-körtid. &quot;Underlag&quot; = antal
          uppdrag bakom raden; rader med tunt underlag skapas inte utan faller tillbaka
          på en grövre nivå.
        </SectionHint>
      </div>
      {loadingMoments ? (
        <LoadingSpinner />
      ) : sortedMoments.length === 0 ? (
        <SectionHint>
          Inga schabloner beräknade ännu — jobbet körs måndagar 21:30, och kräver att
          uppdragen hunnit få Google-körtider sparade.
        </SectionHint>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Casetyp</th>
              <th>Uppdragstyp</th>
              <th>Nivå</th>
              <th style={{ textAlign: 'right' }}>Momenttid</th>
              <th style={{ textAlign: 'right' }}>Underlag</th>
            </tr>
          </thead>
          <tbody>
            {sortedMoments.map((row) => (
              <tr key={row.id}>
                <td>
                  {row.caseTypeID != null
                    ? translations.caseTypes?.[row.caseTypeID]
                    : 'Alla'}
                </td>
                <td>
                  {row.assignmentTypeID != null
                    ? translations.assignmentTypes?.[row.assignmentTypeID]
                    : 'Alla'}
                </td>
                <td>{scopeOf(row)}</td>
                <td className="num">{Math.round(row.medianMomentMinutes)} min</td>
                <td className="num">{row.sampleSize} uppdrag</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div>
        <SectionTitle>Historiska snittider per sträcka</SectionTitle>
        <SectionHint>
          Snitt av total arbetstid (körning + moment) per casetyp, uppdragstyp och
          sträcka (län/stad/postnummerprefix). Används som reservkälla när Google-körtid
          saknas.
        </SectionHint>
      </div>
      <Controls>
        <Input
          onChange={(e) => setMonth(e.currentTarget.value)}
          title="Månad"
          type="month"
          value={month}
        />
        <Input
          onChange={(e) => setRouteFilter(e.currentTarget.value)}
          placeholder="Filtrera på ort/län/postnr…"
          value={routeFilter}
        />
      </Controls>
      {loadingAverages ? (
        <LoadingSpinner />
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Casetyp</th>
              <th>Uppdragstyp</th>
              <th>Från → Till</th>
              <th style={{ textAlign: 'right' }}>Snitt</th>
              <th style={{ textAlign: 'right' }}>Min–Max</th>
              <th style={{ textAlign: 'right' }}>Underlag</th>
            </tr>
          </thead>
          <tbody>
            {filteredAverages.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={index}>
                <td>{translations.caseTypes?.[row.caseTypeID]}</td>
                <td>{translations.assignmentTypes?.[row.assignmentTypeID]}</td>
                <td>
                  {row.fromCounty} ({row.fromZipCode}xx) → {row.toCounty} (
                  {row.toZipCode}xx)
                </td>
                <td className="num">{Math.round(row.averageDuration)} min</td>
                <td className="num">
                  {Math.round(row.minDuration)}–{Math.round(row.maxDuration)} min
                </td>
                <td className="num">{row.sampleSize}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
};

export default EstimateDurationsPage;
